import { filterOptions, normalizeLabel } from "./filterOptions";
import type { SelectOption } from "./selectOption";

/** Options skipped by PageUp and PageDown. */
export const PAGE_STEP = 8;

/** Next enabled index from `from` moving by `step`, clamped at the ends (listboxes do not wrap). */
export function stepEnabled(options: SelectOption[], from: number, step: 1 | -1): number {
  for (let i = from + step; i >= 0 && i < options.length; i += step) if (!options[i]?.disabled) return i;
  return from;
}

export function edgeEnabled(options: SelectOption[], edge: "first" | "last"): number {
  const order = options.map((_, i) => i);
  if (edge === "last") order.reverse();
  return order.find((i) => !options[i]?.disabled) ?? -1;
}

/** Typeahead: the next enabled option after `from` whose label starts with `prefix`. */
export function matchPrefix(options: SelectOption[], from: number, prefix: string): number {
  const p = normalizeLabel(prefix);
  const n = options.length;
  // A repeated single letter cycles; a longer prefix may stay on the current option.
  const start = prefix.length > 1 ? 0 : 1;
  for (let k = start; k < n + start; k++) {
    const i = (from + k + n) % n;
    const o = options[i];
    if (o && !o.disabled && normalizeLabel(o.label).startsWith(p)) return i;
  }
  return -1;
}

/** PageDown: `PAGE_STEP` options further, stopping at the last one. */
export function pageDown(options: SelectOption[], active: number): number {
  return Math.min(options.length - 1, Math.max(0, active) + PAGE_STEP);
}

/** PageUp: `PAGE_STEP` options back, stopping at the first one. */
export function pageUp(active: number): number {
  return Math.max(0, active - PAGE_STEP);
}

/** The option active on opening: the selected one, else the first enabled. */
export function initialActive(options: SelectOption[], selectedIndex: number): number {
  return selectedIndex >= 0 ? selectedIndex : edgeEnabled(options, "first");
}

/** The option active after filtering by `query`: the selected value if still listed, else the first enabled. */
export function activeAfterFilter(options: SelectOption[], query: string, value: string): number {
  const remaining = filterOptions(options, query);
  return initialActive(remaining, remaining.findIndex((o) => o.value === value));
}
